import "./DatePicker.scss";

import React, { forwardRef, useRef } from "react";
import { Popover } from "@metro-ui/core";
import "@mshop-ui/core-web-components/dist/ui-input";
import WebComponentAdapter from "../WebComponentAdapter";
const UiInput = WebComponentAdapter("ui-input");
import { FormGroup, ControlLabel } from "react-bootstrap";
import { isAfter, isValid } from "date-fns";
import { TrackedFormControl, type TrackableProps } from "../common/TrackedComponents";
import {
	CartDeliveryDatePickerStore,
	CheckoutDeliveryDatePickerStore,
	PickupDatePickerStore,
} from "../../stores/date-picker/DatePickerStore";
import { connectStore } from "../../utils/RefluxConnect";
import { DatePickerActions } from "../../actions/DatePickerActions";
import { FormattedMessage } from "react-intl";
import { formatTimeSlots } from "../common/DeliveryTimeSlots";
import { DatePickerWithSlotsPopover } from "./DatePickerWithSlotsPopover";
import { formatLocalDate, formatLocalTime, parseDate } from "../../utils/DateUtil";

import type { PickupDates } from "../../stores/DatesProvider";
import type { Timeslot } from "../../model/CheckoutData";
import type { InvalidPickupDate, ValidDeliveryDate, ValidPickupDate } from "src/app/server/StoreInfoRequests";
import { shouldShowDeliveryDate } from "../../shared/cart-state";

const OverlayTrigger = forwardRef<HTMLElement, TrackableProps>(function OverlayTriggerWithRef(props, ref) {
	return (
		<UiInput class="oc-datepicker-overlaytrigger">
			<TrackedFormControl {...props} innerRef={ref} />
		</UiInput>
	);
});

const PopoverContent = ({
	renderPopover,
	onClose,
}: Pick<DatePickerProps, "renderPopover"> & { onClose: () => void }) => <div>{renderPopover(onClose)}</div>;

/* Generic Date Picker */
type DatePickerProps = {
	selectionText: React.ReactNode;
	isValid: boolean;
	isDisabled: boolean;
	labelMessage?: React.ReactNode;
	renderPopover: (onClose: () => void) => React.ReactNode;
};
export const DatePicker = ({ selectionText, isValid, isDisabled, labelMessage, renderPopover }: DatePickerProps) => {
	const popoverRef = useRef<any>(null);

	return (
		<div className="test-date-picker mfcss_datepicker mfcss_datepicker_v2">
			<FormGroup validationState={!isValid ? "error" : undefined}>
				{labelMessage ? (
					<ControlLabel className={!selectionText ? "mfcss_datepicker_warning_input_label" : ""}>
						{labelMessage}
					</ControlLabel>
				) : null}
				<Popover
					id="date-picker-popover-wrapper"
					className="oc-datepicker-popover-wrapper"
					ref={popoverRef}
					innerComponent={PopoverContent}
					innerProps={{ renderPopover, onClose: () => popoverRef.current?.close() }}
					noCloseButton
				>
					<OverlayTrigger
						tags={{
							source: "DateInputPicker.dateString",
						}}
						type="text"
						readOnly
						className="mfcss_datepicker_input input-field test-date-input text-overflow-ellipsis"
						disabled={isDisabled}
						value={selectionText}
					/>
				</Popover>
			</FormGroup>
		</div>
	);
};
export type PickupDatePickerProps = {
	dates: PickupDates;
	selectedDate: Date | null | undefined;
	selectedTimeslot: Timeslot | null | undefined;
	onSelectDate: (
		date: Date,
		dateInfo: ValidPickupDate | InvalidPickupDate | undefined,
		timeslot: Timeslot | null | undefined,
	) => void;
	variant?: "editable" | "disabled" | "readonly";
	dateFormat: string;
	localDateFormat: string;
	isPickup: boolean;
	earliestItemDates: Set<string> | null | undefined;
	showSuggestions: boolean;
};
const MAX_VALID_DATES_FOR_FSD_DATE_PICKER = 16;
export const DisconnectedPickupDatePicker = ({
	dates,
	selectedDate,
	selectedTimeslot,
	onSelectDate,
	variant,
	dateFormat,
	localDateFormat,
	isPickup,
	earliestItemDates,
	showSuggestions,
	...restProps
}: PickupDatePickerProps) => {
	const isDisabled = variant === "disabled";
	const renderPopover = (onClose) => {
		return (
			<DatePickerWithSlotsPopover
				{...restProps}
				dates={dates}
				selectedDate={selectedDate}
				selectedTimeslot={selectedTimeslot}
				onSelectDate={(date, timeslot) => {
					const dateInfo = dates.get(formatLocalDate(date, dateFormat));

					if (dateInfo && dateInfo.type === "ValidDate") {
						onClose();
					}

					onSelectDate(date, dateInfo, timeslot);
				}}
				onClose={onClose}
				dateFormat={dateFormat}
				localDateFormat={localDateFormat}
				isPickup={isPickup}
				earliestItemDates={earliestItemDates}
				showSuggestions={showSuggestions}
				maxValidDatesForFsdPicker={MAX_VALID_DATES_FOR_FSD_DATE_PICKER}
			/>
		);
	};

	const isValidSelection = (): boolean => {
		if (isDisabled) return true;
		const info = selectedDate && dates.get(formatLocalDate(selectedDate, dateFormat));
		return !!info && info.type === "ValidDate" && !isAfter(new Date(), parseDate(info.validUntil));
	};

	function getDeliveryInputText() {
		const selectedDateInfo = selectedDate && dates.get(formatLocalDate(selectedDate, dateFormat));
		const timeSlots =
			(selectedDateInfo && selectedDateInfo.type === "ValidDate" && (selectedDateInfo as any).deliveryTimes) ||
			[];

		if (!selectedDate) {
			return "";
		}
		const timeslotsString = timeSlots.length ? ` (${formatTimeSlots(selectedDate, timeSlots)})` : "";
		return `${formatLocalDate(selectedDate, localDateFormat)}${timeslotsString}`;
	}

	function getPickupInputText() {
		const dateSelectionText =
			!selectedDate || !isValid(selectedDate) ? "" : formatLocalDate(selectedDate, localDateFormat);
		return selectedTimeslot
			? `${dateSelectionText} (${formatLocalTime(selectedTimeslot.fromUtc)} - ${formatLocalTime(
					selectedTimeslot.untilUtc,
				)})`
			: dateSelectionText;
	}

	const dateSelectionText = isPickup ? getPickupInputText() : getDeliveryInputText();
	const labelMessage = isPickup ? (
		<FormattedMessage id="ORC_DATE_PICKER_LABEL_PICKUP" />
	) : (
		<FormattedMessage id="ORC_LASTCHECK_DELIVERY_DATE_LABEL" />
	);
	return (
		<DatePicker
			{...restProps}
			selectionText={dateSelectionText}
			isDisabled={isDisabled}
			isValid={isValidSelection()}
			labelMessage={labelMessage}
			renderPopover={renderPopover}
		/>
	);
};
export const CheckoutDeliveryDatePicker = connectStore(
	(props) => <DisconnectedPickupDatePicker {...(props as any)} dateFormat="yyyy-MM-dd" localDateFormat="P" />,
	CheckoutDeliveryDatePickerStore,
	{
		onSelectDate: (date: Date, info: ValidDeliveryDate) => DatePickerActions.setCheckoutDeliveryDate(date, info),
	},
);
export const CartDeliveryDatePickerFOE = connectStore(
	(props) => (
		<DisconnectedPickupDatePicker {...(props as any)} dateFormat="yyyy-MM-dd" localDateFormat="P" showSuggestions />
	),
	CartDeliveryDatePickerStore,
	{
		onSelectDate: (date: Date, info: ValidDeliveryDate) => DatePickerActions.setCartDeliveryDate(date, info),
	},
);
export const CartDeliveryDatePicker = connectStore(
	(props) => {
		if (shouldShowDeliveryDate()) {
			return (
				<DisconnectedPickupDatePicker
					{...(props as any)}
					dateFormat="yyyy-MM-dd"
					localDateFormat="P"
					showSuggestions
				/>
			);
		}
		return null;
	},
	CartDeliveryDatePickerStore,
	{
		onSelectDate: (date: Date, info: ValidDeliveryDate) => DatePickerActions.setCartDeliveryDate(date, info),
	},
);
export const PickupDatePicker = connectStore(
	(props) => (
		<DisconnectedPickupDatePicker {...(props as any)} dateFormat="yyyy-MM-dd" localDateFormat="P" isPickup />
	),
	PickupDatePickerStore,
	{
		onSelectDate: (date: Date, info: ValidPickupDate, timeslot: Timeslot | null | undefined) =>
			DatePickerActions.setPickupDate(date, info, timeslot),
	},
);
